import React, { useEffect, useRef, useState } from 'react'
import { cx } from '@linaria/core'
import { ButtonGroup, Checkbox, Drawer, InputGroup } from '@reapitform/common-ui'
import { useDragAndDrop } from '@reapitform/hooks'
import { Button } from '@reapit/elements/core/button'
import { AdminPdfTemplate } from '../../../../api/types'
import { useUpdateAdminPdfTemplate, useUploadAdminPdfTemplate } from '../../../../api/pdf-templates'
import { FieldHelp, FieldStack, SectionHeading } from '../../../../components/form-details-fields/style'
import { PdfDropzone, PdfSelectedFile, PdfSelectedFileName, PdfSelectedFileSize } from './style'

const MAX_PDF_BYTES = 25 * 1024 * 1024

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.round(kb)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

interface PdfTemplateDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  formReferenceID: string
  versionReferenceID: string
  /** The template being edited, or null to upload a new one. */
  template: AdminPdfTemplate | null
  onSaved: () => void
}

/**
 * Upload a new PDF template (multipart file + flags) or edit an existing one's name/flags. On upload the
 * server names the template from the file; it can be renamed here afterwards.
 */
export const PdfTemplateDrawer: React.FC<PdfTemplateDrawerProps> = ({
  isOpen,
  onOpenChange,
  formReferenceID,
  versionReferenceID,
  template,
  onSaved,
}) => {
  const formId = 'pdf-template-form'
  const [upload, uploading] = useUploadAdminPdfTemplate(formReferenceID, versionReferenceID)
  const [update, updating] = useUpdateAdminPdfTemplate(formReferenceID, versionReferenceID, template?.referenceID ?? '')
  const isBusy = uploading || updating

  const [file, setFile] = useState<File | null>(null)
  const [name, setName] = useState('')
  const [isAnnexure, setIsAnnexure] = useState(false)
  const [repeatable, setRepeatable] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const inputRef = useRef<HTMLInputElement>(null)
  const { childHandler, isDragOverZone, registerMainHandler } = useDragAndDrop()

  const acceptFile = (candidate?: File | null) => {
    if (!candidate) return
    if (candidate.type !== 'application/pdf' && !candidate.name.toLowerCase().endsWith('.pdf')) {
      setError('Please choose a PDF file.')
      return
    }
    if (candidate.size > MAX_PDF_BYTES) {
      setError('That PDF is larger than the 25 MB limit.')
      return
    }
    setError(null)
    setFile(candidate)
  }

  useEffect(() => {
    if (!isOpen) return
    setFile(null)
    setError(null)
    setName(template?.name ?? '')
    setIsAnnexure(template?.isAnnexure ?? false)
    setRepeatable(template?.repeatable ?? false)
  }, [isOpen, template])

  const handleClose = () => onOpenChange(false)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    try {
      if (template) {
        await update({ name: name.trim() || null, isAnnexure, repeatable })
      } else {
        if (!file) {
          setError('Choose a PDF file to upload.')
          return
        }
        const formData = new FormData()
        formData.append('file', file)
        formData.append('isAnnexure', String(isAnnexure))
        formData.append('repeatable', String(repeatable))
        await upload(formData)
      }
      onSaved()
      onOpenChange(false)
    } catch {
      // Errors surfaced via the hook toasts.
    }
  }

  const footer = (
    <ButtonGroup alignment="right">
      <Button size="medium" variant="secondary" disabled={isBusy} onClick={handleClose} type="button">
        Cancel
      </Button>
      <Button size="medium" variant="primary" isBusy={isBusy} disabled={isBusy} type="submit" form={formId}>
        {template ? 'Save' : 'Upload'}
      </Button>
    </ButtonGroup>
  )

  return (
    <Drawer
      isOpen={isOpen}
      title={template ? 'Edit PDF Template' : 'Upload PDF Template'}
      onClose={handleClose}
      footerItems={footer}
    >
      {isOpen && (
        <FieldStack id={formId} onSubmit={handleSubmit}>
          {template ? (
            <InputGroup
              label="Name"
              placeholder="e.g. QLD Form 18a"
              value={name}
              onChange={(event) => setName((event.target as HTMLInputElement).value)}
            />
          ) : (
            <div>
              <SectionHeading style={{ marginBottom: '0.75rem' }}>PDF file</SectionHeading>
              <PdfDropzone
                className={cx(isDragOverZone && 'drag-over')}
                onClick={() => inputRef.current?.click()}
                {...registerMainHandler((files) => acceptFile(files[0]))}
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept="application/pdf"
                  onChange={(event) => acceptFile(event.target.files?.[0])}
                  {...childHandler}
                />
                <span {...childHandler}>
                  {isDragOverZone ? (
                    'Drop here to upload'
                  ) : (
                    <>
                      Drag and drop or <span className="action-text">browse files</span>
                    </>
                  )}
                </span>
              </PdfDropzone>
              {file && (
                <PdfSelectedFile>
                  <PdfSelectedFileName>{file.name}</PdfSelectedFileName>
                  <PdfSelectedFileSize>{formatBytes(file.size)}</PdfSelectedFileSize>
                </PdfSelectedFile>
              )}
              <FieldHelp>
                Upload the form or annexure PDF. The file name becomes the template name — you can rename it afterwards.
              </FieldHelp>
              <FieldHelp>File type must be PDF.</FieldHelp>
              <FieldHelp>Maximum file size is 25 MB.</FieldHelp>
            </div>
          )}
          <Checkbox
            label="This is an annexure"
            supplementaryInfo="Leave unticked for the primary form document."
            checked={isAnnexure}
            onChange={(event) => setIsAnnexure((event.target as HTMLInputElement).checked)}
          />
          <Checkbox
            label="Repeatable"
            supplementaryInfo="Can be added more than once (e.g. one per additional party)."
            checked={repeatable}
            onChange={(event) => setRepeatable((event.target as HTMLInputElement).checked)}
          />
          {error && <FieldHelp style={{ color: 'var(--colour-text-danger, #d4285a)' }}>{error}</FieldHelp>}
        </FieldStack>
      )}
    </Drawer>
  )
}
